import {
  Box,
  Button,
  ButtonProps,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Pagination,
  styled,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../Shared-ui/SharedComponents/Breadcrumb";
import ExtraSmallCard from "../../../Shared-ui/SharedComponents/Cards/ExtraSmallCard";
import variables from "../../../assets/styles/variables.module.scss";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { Prompt } from "../../../Shared-ui/Types/common";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import MetaInstance from "../../../Services/AxiosInstance/MetaInstance";
import {
  ShowError,
  ShowSuccess,
} from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import {
  CREATE_PROMPT,
  GET_PROMPTS,
  DELETE_PROMPT,
  FILE_UPLOAD_AGENT,
} from "../../../Services/Endpoint";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";
import Confirm from "../../../Shared-ui/Types/Confirm";
import { isOnlyAlphabets } from "../../../Shared-ui/utils/Common/Common";

const ColorButton = styled(Button)<ButtonProps>(({ theme }) => ({
  // borderRadius: "8px",
  color: variables.whiteColor,
  backgroundColor: variables.primaryButton,
  "&:hover": {
    backgroundColor: variables.primaryColor,
  },
}));

const Prompts = () => {
  const promptInitial = {
    name: "",
    instruction: "",
    description: "",
  };
  const [prompt, setPrompt] = useState<Prompt>(promptInitial);
  const [promptData, setPromptData] = useState<Prompt[]>([]);
  const theme = useTheme();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState<number>(1);
  const prevPage = useRef<number>(page);
  const [promptCount, setPromptCount] = useState<number>(0);
  const [editing, setEditing] = useState<boolean>(false);
  const [confirmPopupData, setConfirmPopupData] = useState<Confirm>({
    show: false,
  });

  const [searchText, setSearchText] = useState("");

  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    setPage(1);
    if (searchText === "") {
      getPrompts();
    }
  }, [searchText]);

  useEffect(() => {
    if (prevPage.current !== page) {
      getPrompts();
      prevPage.current = page;
    }
  }, [page]);

  const [promptErr, setPromptErr] = useState({
    name: { error: "", value: false },
    instruction: { error: "", value: false },
    description: { error: "", value: false },
  });

  // Function to validate prompt form
  const validatePromptForm = () => {
    let isValid = true;

    // Validate Name
    if (!prompt.name.trim()) {
      setPromptErr((prev) => ({
        ...prev,
        name: { error: "Enter the Prompt Name", value: true },
      }));
      isValid = false;
    } else if (prompt.name.length < 5) {
      setPromptErr((prev) => ({
        ...prev,
        name: { error: "Enter at least 5 characters", value: true },
      }));
      isValid = false;
    } else if (!isOnlyAlphabets(prompt.name)) {
      setPromptErr((preVal: any) => ({
        ...preVal,
        name: {
          error: "Numbers and special character are not allowed",
          value: true,
        },
      }));
      isValid = false;
    } else {
      setPromptErr((prev) => ({ ...prev, name: { error: "", value: false } }));
    }

    // Validate Description
    if (!prompt.instruction.trim()) {
      setPromptErr((prev) => ({
        ...prev,
        instruction: { error: "Enter the Prompt Instructions", value: true },
      }));
      isValid = false;
    } else if (prompt.instruction.length < 10) {
      setPromptErr((prev) => ({
        ...prev,
        instruction: { error: "Enter at least 10 characters", value: true },
      }));
      isValid = false;
    } else {
      setPromptErr((prev) => ({
        ...prev,
        instruction: { error: "", value: false },
      }));
    }

    return isValid;
  };

  const handleCreate = async () => {
    if (!validatePromptForm()) return; // Stop execution if validation fails

    try {
      dispatch(StartLoading());
      const name = prompt?.name?.trim();
      const data = await MetaInstance.post(`${CREATE_PROMPT}`, {
        ...prompt,
        name: name,
      }).then((res) => res?.data?.detail);
      dispatch(ShowSuccess(data.response));
      handleClose();
      getPrompts();
    } catch (err: any) {
      dispatch(
        ShowError(err?.response?.data?.detail?.response || "An error occurred")
      );
    } finally {
      dispatch(StopLoading());
    }
  };

  const getPrompts = async () => {
    try {
      dispatch(StartLoading());
      const data = await MetaInstance.get(
        `${GET_PROMPTS}?page=${page}&limit=${pageSize}&search=${searchText}`
      ).then((res) => res?.data?.detail);
      setPromptData(data.response);
      setPromptCount(data.count);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail?.response));
    } finally {
      dispatch(StopLoading());
    }
  };
  // const handleCreate = async () => {

  //   try {
  //     dispatch(StartLoading())
  //     const data = await MetaInstance.post(`${CREATE_PROMPT}`, prompt).then(res => res?.data?.detail)
  //     dispatch(ShowSuccess(data.response))
  //     handleClose()
  //     getPrompts()
  //   } catch (err: any) {
  //     dispatch(ShowError(err?.response?.data?.detail?.response))
  //   } finally {
  //     dispatch(StopLoading())

  //   }
  // }
  const deletePrompt = async (row: Prompt) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading());
      const data = await MetaInstance.delete(
        `${DELETE_PROMPT}?name=${row.name}`
      ).then((res) => res?.data?.detail);
      dispatch(ShowSuccess(data.response));
      getPrompts();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail?.response));
    } finally {
      dispatch(StopLoading());
    }
  };
  const handleUpdate = async () => {
    try {
      dispatch(StartLoading());
      const name = prompt?.name?.trim();
      const data = await MetaInstance.put(
        `${CREATE_PROMPT}?name=${prompt.name}`,
        { ...prompt, name: name }
      ).then((res) => res?.data?.detail);
      dispatch(ShowSuccess(data.response));
      handleClose();
      getPrompts();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail?.response));
    } finally {
      dispatch(StopLoading());
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setPrompt((prevVal) => ({ ...prevVal, [name]: value }));

    if (name === "name") {
      if (value.trim() && value.length >= 5) {
        setPromptErr((prev) => ({
          ...prev,
          name: { error: "", value: false },
        }));
      } else {
        setPromptErr((prev) => ({
          ...prev,
          name: { error: "", value: false },
        }));
      }
    }

    if (name === "instruction") {
      if (value.trim() && value.length >= 10) {
        setPromptErr((prev) => ({
          ...prev,
          instruction: { error: "", value: false },
        }));
      }
    }
  };
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };
  const handleClose = () => {
    setOpen(false);
    setPrompt(promptInitial);
    setPromptErr({
                name: { error: "", value: false },
                instruction: { error: "", value: false },
                description: { error: "", value: false },
                });
    setEditing(false);
  };

  const editPrompt = (row: Prompt) => {
    setEditing(true);
    setPrompt(row);
    setOpen(true);
  };
  const confirmDelete = (row: Prompt) => {
    setConfirmPopupData({
      show: true,
      setConfirmPopupData: setConfirmPopupData,
      title: "Confirm Delete",
      info: "Are you sure want to delete your data? This action cannot be undone.",
      varient: "delete",
      functionName: deletePrompt,
      functionParam: row,
    });
  };
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          //pb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { md: "flex-end", sm: "flex-start" },
            gap: { md: "10px", sm: "0" },
            flexDirection: { md: "row", sm: "column", xs: "column" },
          }}
        >
          <Breadcrumb data={[{ name: "Prompt", path: "" }]} />
        </Box>
        <Box>
          <ColorButton
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            size={isSmallScreen ? "small" : "large"}
            sx={{
              textTransform: "capitalize",
              fontWeight: "400",
              whiteSpace: "nowrap",
              borderRadius: { sm: "8px", xs: "4px" },
              height: "40px",
            }}
            onClick={() => handleClickOpen()}
          >
            Add Prompt
          </ColorButton>
          <Dialog
            fullWidth={true}
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {editing ? "Update Prompt" : "Create Prompt"}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  //width: { md: "500px", sm: "380px", xs: "auto" },
                  pt: "10px",
                }}
              >
                <FormControl
                  fullWidth
                  variant="outlined"
                  //error={loginErr.email.value}
                >
                  <InputLabel
                    htmlFor="agentName"
                    shrink
                    sx={{ background: "#fff", px: 0.5 }}
                  >
                    Prompt Name *
                  </InputLabel>
                  <TextField
                    id="name"
                    name="name"
                    value={prompt.name}
                    placeholder="Enter prompt name"
                    //value={loginForm.email}
                    sx={{ background: "white" }}
                    onChange={handleChange}
                    error={promptErr.name.value}
                    helperText={promptErr.name.error}
                    disabled={editing}
                    inputProps={{
                      maxLength: 25,  
                    }}
                  />
                </FormControl>
                <FormControl
                  fullWidth
                  variant="outlined"
                  //error={loginErr.email.value}
                >
                  <InputLabel
                    htmlFor="instruction"
                    shrink
                    sx={{ background: "#fff", px: 0.5 }}
                  >
                    Instruction *
                  </InputLabel>
                  <TextField
                    required
                    id="instruction"
                    name="instruction"
                    value={prompt.instruction}
                    placeholder="Enter Instructions "
                    multiline
                    rows={3}
                    //value={loginForm.email}
                    sx={{ background: "white" }}
                    onChange={handleChange}
                    error={promptErr.instruction.value}
                    helperText={promptErr.instruction.error}
                  />
                </FormControl>

                <FormControl
                  fullWidth
                  variant="outlined"
                  //error={loginErr.email.value}
                >
                  <InputLabel
                    htmlFor="description "
                    shrink
                    sx={{ background: "#fff", px: 0.5 }}
                  >
                    Description
                  </InputLabel>
                  <TextField
                    required
                    id="description"
                    name="description"
                    value={prompt.description}
                    placeholder="Enter description "
                    multiline
                    rows={3}
                    //value={loginForm.email}
                    sx={{ background: "white" }}
                    onChange={handleChange}
                  />
                </FormControl>
              </Box>
            </DialogContent>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                padding: "24px",
                pt: "0",
              }}
            >
              <Button
                variant="outlined"
                disableElevation
                size="large"
                sx={{
                  textTransform: "capitalize",
                }}
                onClick={handleClose}
              >
                Cancel
              </Button>
              {!editing ? (
                <Button
                  variant="contained"
                  disableElevation
                  size="large"
                  autoFocus
                  sx={{
                    textTransform: "capitalize",
                  }}
                  onClick={handleCreate}
                >
                  Create
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disableElevation
                  size="large"
                  autoFocus
                  sx={{
                    textTransform: "capitalize",
                  }}
                  onClick={handleUpdate}
                >
                  Update
                </Button>
              )}
            </Box>
          </Dialog>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xl: "1fr 1fr 1fr 1fr",
            lg: "1fr 1fr 1fr",
            md: "1fr 1fr",
          },
          gap: 3,
          gridAutoRows: "1fr",
        }}
      >
        {promptData.map((prompt, index) => (
          <Box key={index}>
            <ExtraSmallCard
              data={prompt}
              key={index}
              deleteFun={confirmDelete}
              editFun={editPrompt}
            />
          </Box>
        ))}
      </Box>
      {promptCount > pageSize && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Pagination
            count={Math.ceil(promptCount / pageSize)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
        </Box>
      )}
      <ConfirmPopup details={confirmPopupData} />
    </Container>
  );
};

export default Prompts;
